import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Flag, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const REASONS = [
  { value: 'spam', label: 'Spam or advertising' },
  { value: 'inappropriate', label: 'Inappropriate or offensive' },
  { value: 'gone', label: 'Already gone / sold' },
  { value: 'wrong_location', label: 'Wrong location' },
  { value: 'other', label: 'Something else' },
];

export default function ReportDialog({ open, onOpenChange, pin }) {
  const [reason, setReason] = useState('spam');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/pins/${pin.id}/report`, { reason, note });
      toast.success('Report sent — thanks for keeping the neighborhood safe.');
      setNote('');
      setReason('spam');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Flag className="w-5 h-5 text-[#C86F53]" /> Report this pin
          </DialogTitle>
          <DialogDescription>
            Let admins know why "{pin?.title}" should be reviewed.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={reason} onValueChange={setReason} className="space-y-2 py-2">
          {REASONS.map((r) => (
            <div key={r.value} className="flex items-center gap-3">
              <RadioGroupItem value={r.value} id={`reason-${r.value}`} data-testid={`report-reason-${r.value}`} />
              <Label htmlFor={`reason-${r.value}`} className="text-sm font-normal cursor-pointer">{r.label}</Label>
            </div>
          ))}
        </RadioGroup>

        <Textarea
          data-testid="report-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (optional)"
          className="rounded-xl min-h-[80px]"
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid="report-submit-btn" onClick={submit} disabled={submitting} className="rounded-full bg-[#C86F53] hover:bg-[#b35f45] text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
